import { Box, Fade } from '@mui/material';
import BackButton from '@acorn-portal/shared/src/components/buttons/BackButton';
import PaymentButton from '@acorn-portal/shared/src/components/buttons/PaymentButton';
import ActionButtons from '@acorn-portal/shared/src/components/wrapper/action-buttons';
import ProblemModal from '@acorn-portal/shared/src/components/modal/ProblemModal';
import { Form, Formik } from 'formik';
import { useAppDispatch, useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import {
	PrivateCarClient,
	PaymentClient,
	MakePaymentIntentCommand,
	PcTakeUpQuoteCommand,
	PreparePaymentSource,
	Address,
} from '@acorn-portal/shared/src/services/subscriptionAPI';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { PAYMENT_PROBLEM_MODAL } from '@acorn-portal/shared/src/utils/modalText';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import LoadingModal from '@acorn-portal/shared/src/components/modal/LoadingModal';
import BraintreePaymentForm, {
	BRAINTREE_FORM_INITIAL_VALUES,
	IBraintreeCardPaymentFormValues,
	braintreeCardFormValidationSchema,
} from '@acorn-portal/shared/src/components/forms/braintree-payment-form';
import DirectPaymentVendors from '@acorn-portal/shared/src/components/vendor/DirectPaymentVendors';
import { useBraintreeContext } from '@acorn-portal/shared/src/context/braintree-context';
import { ExtendPaymentMethodOptions } from '@acorn-portal/shared/src/utils/types/extensions/braintree';

import API from '@acorn-portal/shared/src/services/AxiosInstance';
import { useAuth } from 'react-oidc-context';
import { setTakeUpDetails } from '@acorn-portal/shared/src/store/RestartTakeUp.slice';
import { FormikLogErrors } from '@acorn-portal/shared/src/pages/mta/components/FormikLogErrors';

interface IProps {
	payInFullAmount: number;
	toggleModal: (open: boolean) => void;
}

const handleCardHolderAddress = (address: Address): Address => {
	const line1Check = address.line1;
	if (line1Check && line1Check.length > 50) {
		const line1 = line1Check.split(',')[0];
		const line2 = line1Check.split(',')[1].startsWith(' ')
			? line1Check.split(',')[1].substring(1)
			: line1Check.split(',')[1];
		const line3 = address.line2;

		return {
			...address,
			line1,
			line2,
			line3,
		};
	}
	return address;
};

const RestartBraintreeCardPaymentSection = ({ payInFullAmount, toggleModal }: IProps) => {
	const auth = useAuth();
	const [problem, setProblem] = useState(false);
	const [localLoading, setLocalLoading] = useState(false);
	const { instance, isPaymentDetailsValid, selectedPaymentMethodType } = useBraintreeContext();
	const navigate = useNavigate();
	const subscriptionRestart = useAppSelector((state) => state.subscription);
	const dispatch = useAppDispatch();
	const isMounted = useRef(true);
	const emailAddress = auth.user?.profile.email ?? '';

	const handleBraintreePaymentProblem = async () => {
		setProblem(true);
		instance?.clearSelectedPaymentMethod();
	};

	const onSubmitBraintreeForm = async (values: IBraintreeCardPaymentFormValues) => {
		setLocalLoading(true);
		const { saveCard } = values;

		if (!instance || !payInFullAmount) {
			setProblem(true);
			return;
		}

		try {
			const { policyToken } = subscriptionRestart;
			if (!policyToken) {
				throw new Error('No policy token found');
			}
			const endpoint = import.meta.env.VITE_SUBSCRIPTION_API;
			const subscriptionClient = new PaymentClient(endpoint, API);
			const body: MakePaymentIntentCommand = {
				quoteReference: subscriptionRestart?.quoteData?.quoteResponse.quoteRef ?? '',
				productType: 'PrivateCar', // Add the required productType property
				paymentDetails: {
					saveCard: saveCard,
					currencyCode: 'GBP',
					amount: payInFullAmount,
					billingAddress: {
						line1: subscriptionRestart.quoteData?.quoteDetails.riskData.proposerDetails.address.line1 ?? '',
						line2: subscriptionRestart.quoteData?.quoteDetails.riskData.proposerDetails.address.line2 ?? '',
						line3: subscriptionRestart.quoteData?.quoteDetails.riskData.proposerDetails.address.line3 ?? '',
						line4: subscriptionRestart.quoteData?.quoteDetails.riskData.proposerDetails.address.line4 ?? '',
						city: subscriptionRestart.quoteData?.quoteDetails.riskData.proposerDetails.address.city ?? '', // Defaulted to Liverpool as the return address from the API is faulty
						postcode: subscriptionRestart.quoteData?.quoteDetails.riskData.proposerDetails.address.postcode ?? '',
						country: subscriptionRestart.quoteData?.quoteDetails.riskData.proposerDetails.address.country ?? '',
						county: subscriptionRestart.quoteData?.quoteDetails.riskData.proposerDetails.address.county ?? '',
					},
					cardHolderDetails: {
						firstName: subscriptionRestart.quoteData?.quoteDetails.riskData.driverDetails[0].firstName ?? '',
						lastName: subscriptionRestart.quoteData?.quoteDetails.riskData.driverDetails[0].lastName ?? '',
						email: emailAddress, // TODO: This should be the cardholder email, not the policyholder email
					},
					clientReturnURL: 'https://hello-world.com', //Really???
				},
				quoteToken: policyToken,
				preparePaymentSource:
					selectedPaymentMethodType === 'PayPalAccount' ? PreparePaymentSource.WALLET : PreparePaymentSource.CARD,
			};
			const paymentIntent = await subscriptionClient.makePaymentIntent(body);

			const fixedAddress = handleCardHolderAddress(values.cardholderAddress);

			if (!paymentIntent) throw Error('Unable to make payment intent');

			const requestPayload: ExtendPaymentMethodOptions = {
				threeDSecure: {
					amount: payInFullAmount.toString(),
					challengeRequested: values.saveCard,
					email: values.cardholderEmail,
					billingAddress: {
						givenName: values.cardholderFirstName,
						surname: values.cardholderLastName,
						streetAddress: fixedAddress.line1 ?? '',
						extendedAddress: fixedAddress.line2 ?? undefined,
						line3: fixedAddress.line3 ?? undefined,
						locality: fixedAddress.city ?? undefined,
						postalCode: fixedAddress.postcode ?? '',
					},
					additionalInformation: {
						ipAddress: paymentIntent.clientIp,
					},
				},
			};
			const braintreeResponse = await instance.requestPaymentMethod(requestPayload);

			if (!braintreeResponse) {
				return handleBraintreePaymentProblem();
			}

			if (!braintreeResponse.deviceData) {
				return handleBraintreePaymentProblem();
			}

			if (
				braintreeResponse.type === 'CreditCard' &&
				(!braintreeResponse.threeDSecureInfo?.liabilityShifted ||
					!braintreeResponse.threeDSecureInfo?.liabilityShiftPossible)
			) {
				return handleBraintreePaymentProblem();
			}

			const takeupDetails: PcTakeUpQuoteCommand = {
				quoteReference: subscriptionRestart?.quoteData?.quoteResponse.quoteRef ?? '',
				paymentCollectionOptionCode: 'SHORT_TERM_CARD_PAYMENT_V1',
				nonce: braintreeResponse.nonce,
				deviceData: braintreeResponse.deviceData ?? '',
				idempotenceKey: paymentIntent.idempotencyKey,
				sendWelcomePack: false,
				paymentId: paymentIntent.paymentId,
				customerBillingDetails: {
					firstName: values.cardholderFirstName,
					lastName: values.cardholderLastName,
					address: {
						line1: fixedAddress.line1 ?? '',
						line2: fixedAddress.line2 ?? '',
						line3: fixedAddress.line3 ?? '',
						line4: fixedAddress.line4 ?? '',
						city: fixedAddress.city ?? '', // Defaulted this as the return address from the API is faulty
						postcode: fixedAddress.postcode ?? '',
						country: fixedAddress.country ?? '',
						county: fixedAddress.county ?? '',
					},
				},
				automaticRenewal: false,
				scheme: subscriptionRestart.quoteData?.quoteResponse.underwrittenOptions?.[0]?.scheme ?? '',
			};

			const url = import.meta.env.VITE_SUBSCRIPTION_API;
			const privateCarClient = new PrivateCarClient(url, API);
			const takeUpResponse = await privateCarClient.takeUpQuote(takeupDetails);
			const date = new Date(subscriptionRestart.quoteData?.quoteResponse.effectiveFrom ?? '');

			dispatch(
				setTakeUpDetails({
					policyNumber: takeUpResponse.policyNumber,
					sendWelcomePackStatus: takeUpResponse.sendWelcomePackStatus,
					startTime: date,
				}),
			);
			navigate(ROUTE_PATHS.subscriptionRestartPaymentSuccess);
		} catch (error) {
			console.error('Error processing payment:', error);
			navigate(ROUTE_PATHS.error);
		} finally {
			if (isMounted.current) {
				setLocalLoading(false);
			}
		}
	};

	if (subscriptionRestart.quoteData?.quoteDetails.riskData.driverDetails.length === 0) return null;
	return (
		<Fade in>
			<Box>
				<LoadingModal open={localLoading} message='Processing Payment' />
				<Box sx={{ width: '100%' }}>
					<Formik<IBraintreeCardPaymentFormValues>
						initialValues={BRAINTREE_FORM_INITIAL_VALUES}
						onSubmit={onSubmitBraintreeForm}
						validationSchema={braintreeCardFormValidationSchema}
					>
						{() => {
							return (
								<Form>
									<FormikLogErrors />
									<Box
										sx={{
											textAlign: 'left',
											margin: 'auto',
										}}
									>
										<BraintreePaymentForm
											policyHolder={{
												address: {
													line1:
														subscriptionRestart.quoteData?.quoteDetails.riskData.proposerDetails.address.line1 ?? '',
													line2:
														subscriptionRestart.quoteData?.quoteDetails.riskData.proposerDetails.address.line2 ?? '',
													line3:
														subscriptionRestart.quoteData?.quoteDetails.riskData.proposerDetails.address.line3 ?? '',
													line4:
														subscriptionRestart.quoteData?.quoteDetails.riskData.proposerDetails.address.line4 ?? '',
													city: subscriptionRestart.quoteData?.quoteDetails.riskData.proposerDetails.address.city ?? '',
													postcode:
														subscriptionRestart.quoteData?.quoteDetails.riskData.proposerDetails.address.postcode ?? '',
													country:
														subscriptionRestart.quoteData?.quoteDetails.riskData.proposerDetails.address.country ?? '',
													county:
														subscriptionRestart.quoteData?.quoteDetails.riskData.proposerDetails.address.county ?? '',
												},
												firstName:
													subscriptionRestart.quoteData?.quoteDetails.riskData.driverDetails[0].firstName ?? '',
												lastName: subscriptionRestart.quoteData?.quoteDetails.riskData.driverDetails[0].lastName ?? '',
												email: emailAddress,
											}}
											amountToPay={payInFullAmount?.toString() ?? ''}
											saveCardWarning='We will not be able to take your next Rolling Subscription payment automatically, if you opt out
											to saving your card details.'
										/>
									</Box>
									<Box
										sx={{
											paddingTop: 2,
											maxWidth: '400px',
											textAlign: 'center',
											margin: 'auto',
										}}
									>
										<DirectPaymentVendors />
									</Box>
									<Box sx={{ paddingTop: 2 }}>
										<ActionButtons>
											<BackButton onClick={() => toggleModal(true)}>Back</BackButton>
											<PaymentButton type='submit' disabled={!isPaymentDetailsValid} icon='Card'>
												Make Payment
											</PaymentButton>
										</ActionButtons>
									</Box>
								</Form>
							);
						}}
					</Formik>
				</Box>
				<ProblemModal
					open={problem}
					title={PAYMENT_PROBLEM_MODAL.header}
					description={PAYMENT_PROBLEM_MODAL.text}
					onClose={() => setProblem(false)}
				/>
			</Box>
		</Fade>
	);
};

export default RestartBraintreeCardPaymentSection;
